// src/components/reports/MeasureCard.tsx — card chrome shared by every measure:
// icon + label on the left, the home-pin on the right, the figure(s) below.
// Ported from the prototype reports-app.jsx.

import type { ReactNode } from "react";
import { C, F } from "../../theme";
import { Card } from "../ui/Card";
import { PinButton } from "./PinButton";
import { Trend } from "./Trend";
import type { MeasureId } from "../../lib/reports";

type Tone = "brass" | "heather" | "sage";
type Glyph = (p: { s?: number; c?: string }) => ReactNode;

export const MeasureCard = ({
  measureId,
  icon: Ic,
  label,
  tone = "brass",
  pinnedId,
  onPin,
  children,
}: {
  measureId: MeasureId;
  icon: Glyph;
  label: string;
  tone?: Tone;
  pinnedId: MeasureId | null;
  onPin: (id: MeasureId | null) => void;
  children: ReactNode;
}) => {
  const tint = { brass: C.brassBg, heather: C.heatherBg, sage: C.sageBg }[tone];
  const ink = { brass: C.brassDk, heather: C.heatherDk, sage: C.sageDk }[tone];
  const pinned = pinnedId === measureId;
  return (
    <Card padded={false} edge={false} style={{ overflow: "hidden", borderColor: pinned ? C.brassLt : C.hairlineLt }}>
      {pinned && <div style={{ height: 3, background: `linear-gradient(90deg, ${C.brassDk}, ${C.brassLt} 70%, transparent)` }} />}
      <div style={{ padding: "13px 14px 0", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: tint, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Ic s={20} c={ink} />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
          <div style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: -0.2, lineHeight: 1.15 }}>{label}</div>
        </div>
        <PinButton measureId={measureId} pinnedId={pinnedId} onToggle={onPin} />
      </div>
      <div style={{ padding: "10px 14px 15px" }}>{children}</div>
    </Card>
  );
};

export const BigFig = ({ value, sub, delta }: { value: string; sub?: string; delta?: number }) => (
  <div>
    <div style={{ fontFamily: F.serif, fontSize: 34, fontWeight: 800, color: C.ink, letterSpacing: -0.8, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {sub && <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkMute, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 6 }}>{sub}</div>}
    {delta !== undefined && <div style={{ marginTop: 8 }}><Trend delta={delta} /></div>}
  </div>
);
